import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  GraduationCap,
  CheckCircle,
  FolderOpen,
  ShieldCheck,
  Upload,
  Phone,
  ClipboardCheck,
  MapPin,
  Mail,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// ── Your hero images ── add / replace as needed ──────────────
import bursaryKiegoi from "../assets/bursary-kiegoi.jpg";
import bursaryIssuance from "../assets/bursary-kirindine.jpg"
import bursaryIssue from "../assets/bursary-issue.jpg"
// import heroImg2 from "../assets/hero-2.jpg";
// import heroImg3 from "../assets/hero-3.jpg";

import StoriesSection from "../components/StoriesSection";
import ServicesSection from "../components/ServiceSection";

/* ─────────────────────────────────────────────────────────────
   HERO SLIDES  ·  swap the `image` values with your real imports
   ───────────────────────────────────────────────────────────── */
const heroSlides = [
  {
    image: bursaryKiegoi,                 // replace when ready
    heading: "Igembe South Constituency Bursary Portal",
    sub: "Apply for bursary support easily and track your application progress online — faster, transparent, and paperless.",
  },
  {
    image: bursaryIssuance,                 // replace with heroImg2
    heading: "Empowering Students Across Igembe South",
    sub: "We are committed to ensuring every deserving student gets the financial support they need to succeed.",
  },
  {
    image: bursaryIssue,                 // replace with heroImg3
    heading: "Transparent, Paperless & Fast",
    sub: "Submit documents, track approvals and receive disbursements — all from the comfort of your phone or computer.",
  },
];

/* ─── Scroll-reveal hook ─────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("sr-visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".sr").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ─── Counter-up hook ────────────────────────────────────────── */
function useCounters() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !e.target.dataset.done) {
            e.target.dataset.done = "true";
            const target = +e.target.dataset.target;
            const duration = 1800;
            let start = null;
            const tick = (ts) => {
              if (!start) start = ts;
              const p = Math.min((ts - start) / duration, 1);
              e.target.textContent = Math.round(
                (1 - Math.pow(1 - p, 3)) * target
              ).toLocaleString();
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.5 }
    );
    document.querySelectorAll("[data-target]").forEach((el) =>
      observer.observe(el)
    );
    return () => observer.disconnect();
  }, []);
}

/* ═══════════════════════════════════════════════════════════════
   HOME COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const Home = () => {
  useScrollReveal();
  useCounters();

  /* Hero slideshow */
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  

  const goTo = (index) => {
    if (fading) return;
    setFading(true);
    setTimeout(() => {
      setCurrent(((index % heroSlides.length) + heroSlides.length) % heroSlides.length);
      setFading(false);
    }, 350);
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

 useEffect(() => {
  const id = setInterval(() => {
    setCurrent((prev) => (prev + 1) % heroSlides.length);
  }, 5500);

  return () => clearInterval(id);
}, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Spacer / Separator below Navbar */}
      <div className="h-16 w-full bg-white border-b border-gray-300" />

      {/* ================================================= */}
      {/* HERO — sliding background images                  */}
      {/* ================================================= */}
      <section className="relative text-white overflow-hidden min-h-[520px]">

        {/* Slides (cross-fade) */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
            style={{
              backgroundImage: `url(${slide.image})`,
              opacity: i === current && !fading ? 1 : 0,
              zIndex: i === current ? 1 : 0,
            }}
          />
        ))}

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60 z-10" />

        {/* Text content */}
        <div className="relative z-20 max-w-6xl mx-auto px-6 py-32 text-center">
          <h1
            key={`h-${current}`}
            className="text-4xl md:text-5xl font-bold mb-4 animate-hero-up"
          >
            {heroSlides[current].heading}
          </h1>
          <p
            key={`p-${current}`}
            className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto animate-hero-up-delay"
          >
            {heroSlides[current].sub}
          </p>

          {/* CTA row */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-hero-up-delay2">
            <Link
              to="/login"
              className="bg-blue-700 hover:bg-blue-600 active:scale-95 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 hover:scale-105 inline-flex items-center gap-2"
            >
              Start Application <ArrowRight size={18} />
            </Link>
            <Link
              to="/login"
              className="bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/40 active:scale-95 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 hover:scale-105 inline-flex items-center gap-2"
            >
              Check Status <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {/* Prev / Next arrows */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/35 backdrop-blur-sm text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/35 backdrop-blur-sm text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
        >
          <ChevronRight size={20} />
        </button>

        {/* Dot indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={`transition-all duration-300 rounded-full ${
                i === current
                  ? "w-8 h-2.5 bg-white"
                  : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ================================================= */}
      {/* STATS BAR                                         */}
      {/* ================================================= */}
      <div className="bg-stats flex flex-wrap justify-center">
        {[
          { value: 8400000, label: "Applications Processed" },
          { value: 98,   label: "% Satisfaction Rate"    },
          { value: 15,   label: "Million KES Disbursed"  },
          { value: 1,    label: "Funding Cycle / Year"  },
        ].map((s, i) => (
          <div
            key={i}
            className="sr sr-up flex-1 min-w-[160px] text-center py-5 px-4 border-r border-white/15 last:border-r-0"
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <span className="block text-3xl font-bold text-white" data-target={s.value}>0</span>
            <span className="block text-xs uppercase tracking-widest text-white/70 mt-1">{s.label}</span>
          </div>
        ))}
      </div>

      <ServicesSection />

      {/* ================================================= */}
      {/* QUICK SERVICES                                    */}
      {/* ================================================= */}
      <section className="py-20 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">

          <h2 className="sr sr-up text-3xl md:text-4xl font-bold text-center mb-12">
            Quick Services
          </h2>

          <div className="grid md:grid-cols-2 gap-10">

            {/* APPLY */}
            <div className="sr sr-left group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-10 flex flex-col border border-gray-200 hover:border-blue-300 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-700 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              <div className="flex justify-center mb-6 text-blue-700">
                <GraduationCap size={56} className="group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
              </div>
              <h3 className="text-2xl font-semibold text-center mb-4">Apply for Bursary</h3>
              <p className="text-gray-600 text-center mb-10 flex-grow">
                Start a new bursary application by submitting your personal,
                academic and supporting documents online.
              </p>
              <Link
                to="/login"
                className="group/btn bg-blue-700 text-white font-semibold py-3 px-5 rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-200 inline-flex items-center justify-between"
              >
                <span>Start Application</span>
                <ArrowRight size={18} className="ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>

            {/* STATUS */}
            <div className="sr sr-right group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-10 flex flex-col border border-gray-200 hover:border-gray-400 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-900 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              <div className="flex justify-center mb-6 text-blue-700">
                <ClipboardCheck size={56} className="group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300" />
              </div>
              <h3 className="text-2xl font-semibold text-center mb-4">Check Application Status</h3>
              <p className="text-gray-600 text-center mb-10 flex-grow">
                Login to track approvals, updates and bursary disbursement
                progress in real-time.
              </p>
              <Link
                to="/login"
                className="group/btn bg-gray-900 text-white font-semibold py-3 px-5 rounded-xl hover:bg-gray-800 active:scale-95 transition-all duration-200 inline-flex items-center justify-between"
              >
                <span>Login to Continue</span>
                <ArrowRight size={18} className="ml-3 group-hover/btn:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* ELIGIBILITY & REQUIREMENTS                        */}
      {/* ================================================= */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">

          <div className="sr sr-up text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Eligibility & Requirements
            </h2>
            <p className="text-gray-600">
              Review the criteria and documents required before submitting your bursary application.
            </p>
            <div className="w-24 h-1 bg-blue-700 mx-auto rounded-full mt-6" />
          </div>

          <div className="grid md:grid-cols-2 gap-10">

            {/* Eligibility */}
            <div className="sr sr-left group bg-white rounded-2xl shadow-md border border-gray-200 p-10 flex flex-col hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-700 text-white p-3 rounded-full group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                  <CheckCircle size={22} />
                </div>
                <h3 className="text-2xl font-semibold">Eligibility Criteria</h3>
              </div>
              <ul className="text-gray-700 space-y-4 text-lg flex-grow">
                {["Resident of Igembe South Constituency", "Enrolled in Secondary, College, or University", "Demonstrated financial need"].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                      <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/eligibility"
                className="mt-8 inline-flex items-center justify-between w-full bg-blue-700 text-white px-5 py-2.5 rounded-lg hover:bg-blue-600 active:scale-95 transition-all duration-200"
              >
                Learn More <ArrowRight size={18} />
              </Link>
            </div>

            {/* Requirements */}
            <div className="sr sr-right group bg-white rounded-2xl shadow-md border border-gray-200 p-10 flex flex-col hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-700 text-white p-3 rounded-full group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                  <FolderOpen size={22} />
                </div>
                <h3 className="text-2xl font-semibold">Required Documents</h3>
              </div>
              <ul className="text-gray-700 space-y-4 text-lg flex-grow">
                {["Birth Certificate or National ID", "Parent/Guardian ID copy", "Admission or School Letter"].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2 w-4 h-4 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
                      <span className="w-2 h-2 rounded-full bg-blue-700 block" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/requirements"
                className="mt-8 inline-flex items-center justify-between w-full bg-blue-700 text-white px-5 py-2.5 rounded-lg hover:bg-blue-600 active:scale-95 transition-all duration-200"
              >
                Learn More <ArrowRight size={18} />
              </Link>
            </div>

          </div>
        </div>
      </section>

      <StoriesSection />

      {/* ================================================= */}
      {/* PROCESS                                           */}
      {/* ================================================= */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="sr sr-up text-3xl md:text-4xl font-bold text-center mb-14">
            Application Process
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <GraduationCap size={28} />, title: "Personal & Academic Info", desc: "Provide personal details, education level, institution name, and course/class info." },
              { icon: <ShieldCheck size={28} />,   title: "Guardian Details",          desc: "Enter parent or guardian info for verification." },
              { icon: <Upload size={28} />,         title: "Document Upload",           desc: "Upload birth certificate/ID, guardian ID, and admission/school letter." },
              { icon: <CheckCircle size={28} />,    title: "Review & Submit",           desc: "Check accuracy, accept declaration, and submit for review." },
            ].map((step, index) => (
              <div
                key={index}
                className="sr sr-up group bg-white rounded-2xl shadow-md p-6 text-center border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300 flex flex-col h-full"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="text-blue-700 font-bold mb-3 text-sm tracking-wider uppercase">
                  Step {index + 1}
                </div>
                <div className="flex justify-center mb-3">
                  <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                    {step.icon}
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-700 text-sm flex-grow leading-snug">{step.desc}</p>
                {index < 3 && <div className="hidden lg:block mt-4 w-full h-0.5 bg-blue-200 mx-auto" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* SUPPORT                                           */}
      {/* ================================================= */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="sr sr-up text-3xl md:text-4xl font-bold mb-3">Need Help?</h2>
          <p className="sr sr-up text-gray-700 text-lg mb-12 max-w-2xl mx-auto">
            If you need guidance or assistance with your bursary application, you can reach out through any of the options below.
          </p>

          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <MapPin size={28} />, title: "Visit Office",   desc: "Constituency Bursary Office, Igembe South. Monday – Friday, 8:00 AM – 5:00 PM.", label: "Get Directions",   href: "https://goo.gl/maps/example" },
              { icon: <Phone size={28} />,  title: "Call Us",        desc: "Contact our bursary office directly for assistance or inquiries.",                  label: "Call Now",        href: "tel:+254700000000" },
              { icon: <Mail size={28} />,   title: "Email Support",  desc: "Send us an email and our support team will respond promptly.",                      label: "Email Us",        href: "mailto:bursary@igembesouth.go.ke" },
            ].map((card, i) => (
              <div
                key={i}
                className="sr sr-up group bg-white rounded-2xl shadow-md p-8 flex flex-col items-center text-center border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300 relative overflow-hidden"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                {/* ripple fill */}
                <div className="absolute inset-0 bg-blue-50 scale-0 group-hover:scale-100 transition-transform duration-500 rounded-2xl origin-center" />
                <div className="relative z-10 bg-blue-700 text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300">
                  {card.icon}
                </div>
                <h3 className="relative z-10 text-xl font-semibold mb-2">{card.title}</h3>
                <p className="relative z-10 text-gray-700 mb-4 text-sm flex-grow">{card.desc}</p>
                <a
                  href={card.href}
                  target={card.href.startsWith("https") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="relative z-10 mt-auto bg-blue-700 text-white py-2 px-5 rounded-lg hover:bg-blue-600 active:scale-95 transition-all duration-200 text-sm font-medium"
                >
                  {card.label}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;